import { RefObject, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';

interface Sensor {
  resizeObserver: ResizeObserver;
  sensorCallback: {
    (): void;
    cancel(): void;
  };
}

export const useSensor = <T extends HTMLElement>(callback: () => void): RefObject<T | null> => {

  // callback ref
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // reference to the object to measure
  const ref = useRef<T | null>(null);

  // initiate resize sensor
  // if ref.current points to a DOM element create a new sensor
  const createSensor: () => Sensor = () => {
    if (!ref.current) {
      return undefined;
    }

    const sensorCallback = debounce(() => {
      callbackRef.current();
    }, 100);

    const resizeObserver = new ResizeObserver(sensorCallback);

    resizeObserver.observe(ref.current);

    return {
      resizeObserver,
      sensorCallback
    };
  };

  const sensorRef = useRef<Sensor | null>(null);

  // update sensor and clean up
  // when ref.current is changed detach previous sensor and create a new one
  useEffect(() => {

      sensorRef.current = createSensor();

    return () => {
      sensorRef.current?.resizeObserver?.disconnect();
      sensorRef.current?.sensorCallback?.cancel();
    };
  }, [ref.current]);

  return ref;
};
